import { For, Show, Signal, createEffect, createMemo, createSignal } from "solid-js";
import { FkInput as FKInput, DataStore, Flexbox, Image, Modal, requireDataStoreContext, Button, requireFocusContext, Select, requireViewContext, DataStoreContext } from "st-app";
import { roleDashboard as api } from 'sis-app/api'
import { StudentSelect } from "sis-app";
import { OrgYearInfo } from "st-app/api";
import styles from '../sis-role-dashboard.module.css'

export interface IStudentSelectionInfo {
    orgYear: OrgYearInfo | undefined;
    orgYearGU: string | undefined;
    studentGU: string | undefined;
    ssyGU: string | undefined;
    stuFormattedName: string | undefined;
    focusKey: string | undefined;
}

type StudentSelectionProps = {
    hideFooter?: boolean;
    onClose: (res: IStudentSelectionInfo) => void;
}

export function StudentSelectionModal(props: StudentSelectionProps) {
    const focus = requireFocusContext();
    const dataStore = requireDataStoreContext();
    const viewCtx = requireViewContext();

    const studentGU = createMemo(() => dataStore.get('currentValue', 'K12-Student-StudentGU'));
    const stuFormattedName = createMemo(() => dataStore.get('currentValue', 'K12-Student-JoinByStudentGU-FormattedName', studentGU()));
    const ssyGU = createMemo(() => dataStore.get('currentValue', 'K12-EnrollmentInfo-StudentSOREnrollment-StudentSchoolYearGU', studentGU()));
    const studentFocusKey = createMemo(() => dataStore.get('currentValue', 'K12-Student-FocusKey', studentGU()));

    const [orgYearGU, setOrgYearGU] = createSignal(focus.focusOrgYear()?.orgYearGU || '');
    const orgYear = createMemo(() => {
        const oygu = orgYearGU();
        return focus.orgYears.find(oy => oy.orgYearGU === oygu);
    });
    const orgYearName = createMemo(() => orgYear()?.organizationName ?? '');

    createEffect(() => {
        viewCtx.setServerState('ORG_YEAR_GU', orgYearGU());
    });

    const leafOrgs = createMemo(() => {
        return focus.orgYears.filter(oy => oy.isLeaf);
    });

    const nonLeafOrgs = createMemo(() => {
        const nlOrgsWithLeafs = focus.orgYears.reduce((res, it) => {
            if (it.parentOrganizationGU && it.isLeaf) {
                res[it.parentOrganizationGU] ??= 0;
                res[it.parentOrganizationGU]++;
            }
            return res;
        }, {} as Record<string, number>);

        return focus.orgYears.filter(oy => !oy.isLeaf && nlOrgsWithLeafs[oy.organizationGU ?? ''] > 0);
    });

    function accept() {
        props.onClose({
            orgYear: orgYear(),
            orgYearGU: orgYear()?.orgYearGU,
            studentGU: studentGU(),
            ssyGU: ssyGU(),
            stuFormattedName: stuFormattedName(),
            focusKey: studentFocusKey() || undefined
        });
    }

    return <DataStore context={dataStore} >
        <Flexbox dir="vertical" style="min-width: 600px" >
            <StudentSelect />
            <Show when={!props.hideFooter}>
                <Flexbox middle dir="horizontal" style="margin-top: 15px" >
                    <Flexbox.Item auto />
                    <Flexbox.Item>
                        <Button type="primary" enabled={!!studentGU()} onClick={accept}>Continue</Button>
                    </Flexbox.Item>
                </Flexbox>
            </Show>
        </Flexbox>
    </DataStore>
}

function StudentFKRow() {
    const focus = requireFocusContext();
    const dataStore = requireDataStoreContext();
    const viewCtx = requireViewContext();

    const photoSrc = createMemo(() => dataStore.get('currentValue', 'K12-Student-Photo'));
    const sisNumber = createMemo(() => dataStore.get('currentValue', 'K12-Student-SisNumber'));
    const stuFormattedName = createMemo(() => dataStore.get('currentValue', 'K12-Student-JoinByStudentGU-FormattedName'));
    const gradeLevel = createMemo(() => dataStore.get('currentValue', 'K12-EnrollmentInfo-StudentSOREnrollment-Grade'));

    return <Flexbox dir="horizontal">
        <Flexbox auto dir="vertical">
            <Flexbox.Item style={{ "font-weight": 'bold', 'font-size': '1.2em' }} >
                {stuFormattedName()}
            </Flexbox.Item>
            <Flexbox dir="horizontal" padded style={{ 'color': 'var(--st-dim-fg)', 'font-size': '0.9em' }} >
                <Flexbox.Item>{sisNumber()}</Flexbox.Item>
                <Flexbox.Item>{gradeLevel()}</Flexbox.Item>
            </Flexbox>
        </Flexbox>
        <Flexbox.Item>
            <Image useDefaultPhoto src={photoSrc()} alt="Student Photo" width="80px" style="max-height: 150px" />
        </Flexbox.Item>
    </Flexbox>
}
